// Shared with the client bundle: keep this file free of server-only imports.

/**
 * How success is decided:
 * - vibes:  the LLM decides purely on creativity and plausibility; probability settings are ignored.
 * - hybrid: the LLM decides, using the probability and creativity points as a rough guide.
 * - dice:   the server rolls against the probability, adjusted by creativity points.
 */
export const OUTCOME_MODES = ["vibes", "hybrid", "dice"] as const;
export type OutcomeMode = (typeof OUTCOME_MODES)[number];

/** Max points creativity can add (innovation 100) or remove (innovation 0) from the success chance. */
export const CREATIVITY_POINT_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 40, 50] as const;

/**
 * Which service generates step clips.
 * - machgen: MiniMax H3 with up to 9 reference images ($0.05/s at 480p), best character/location consistency.
 * - masky:   Masky videos ($0.025/s at 720p, $0.015 draft); no reference images, continuity comes from
 *            starting each clip on the previous clip's last frame.
 */
export const VIDEO_PROVIDERS = ["fal-turbo", "fal-turbo-half", "fal", "machgen", "gmi", "masky"] as const;
export type VideoProvider = (typeof VIDEO_PROVIDERS)[number];

/** No-video mode: how long the scene text stays up in place of a step clip (matches the 15s clips). */
export const SCENE_TEXT_SECS = 15;

/**
 * How much one visitor may do, counted either in finished games or in generated steps.
 *
 * Two of these are configured: one for guests (where running out means "sign in") and one for signed-in
 * members (where running out means "that's your lot for now"). Games and generations are alternatives, not
 * both: when the mode is "games" the generation number is ignored entirely, which is what makes "one full
 * story, however many steps it takes" expressible.
 */
export const ALLOWANCE_MODES = ["unlimited", "games", "generations"] as const;
export type AllowanceMode = (typeof ALLOWANCE_MODES)[number];
export type Allowance = {
  mode: AllowanceMode;
  count: number;
  /**
   * Days before the allowance refills. The clock starts at the viewer's first move of a window, so it is a
   * rolling window per person, not a global reset hour that everyone races. 0 means it never refills.
   */
  resetDays: number;
};

/** Sanity cap for the admin inputs. Not a business rule - just keeps a typo from reading as unlimited. */
export const ALLOWANCE_MAX = 99;
export const RESET_DAYS_MAX = 365;
export const SHARE_BONUS_MAX = 20;

/**
 * What players can buy once their allowance runs out, if anything: packs of generated steps, or whole games
 * (a paid game lets them keep generating until that story ends). Only one is offered at a time.
 */
export const PURCHASE_MODES = ["off", "generations", "games"] as const;
export type PurchaseMode = (typeof PURCHASE_MODES)[number];
export const PACK_MAX = 1000;
/** How many sizes may be offered at once: enough to choose from, few enough to read at a glance. */
export const PACK_SIZES_MAX = 6;
export const DEFAULT_PACK_SIZES = [10, 50, 100, 500];
/** Stories are bought in bigger steps than scenes: one is a whole escape attempt. */
export const DEFAULT_GAME_PACK_SIZES = [1, 5, 10, 20, 50, 100];

/** Stripe will not charge less than this in USD; a pack priced below it is raised to it. */
export const STRIPE_MIN_CHARGE_USD = 0.5;

export type Quote = {
  mode: Exclude<PurchaseMode, "off">;
  /** Units in one purchase (generations, or games). */
  units: number;
  priceUsd: number;
  /** What the arithmetic alone produced, before the manual nudge. */
  computedUsd: number;
  /** The nudge that was applied, so the panel can show where the final price came from. */
  adjustUsd: number;
  stripeFeeUsd: number;
  costUsd: number;
  profitUsd: number;
  profitPerUnitUsd: number;
  /** Profit per generation, the figure the admin setting is expressed in. */
  profitPerGenerationUsd: number;
};

/**
 * The price of one pack, and what it leaves after costs and Stripe. Stripe's fee is charged on the final
 * price, so it is solved for rather than added on:
 *
 *   price = (units * (cost + profit) + fixedFee) / (1 - percentFee)
 *
 * which leaves exactly `profit` per unit once Stripe has taken its cut; rounding up to the cent only adds.
 * Shared with the client so the admin panel can re-price on every keystroke, with the same arithmetic the
 * server charges by.
 */
export function quotePack(p: {
  mode: Exclude<PurchaseMode, "off">;
  units: number;
  unitCostUsd: number;
  unitProfitUsd: number;
  generationsPerUnit: number;
  feePercent: number;
  feeFixedUsd: number;
  /**
   * Added to (or taken off) the computed price, for landing on a number a person would actually write:
   * $1.63 becomes $1.99 with +0.36. Everything below it is recomputed from the adjusted price, so the
   * profit shown is the profit made.
   */
  adjustUsd?: number;
}): Quote {
  const pct = p.feePercent / 100;
  const raw = (p.units * (p.unitCostUsd + p.unitProfitUsd) + p.feeFixedUsd) / (1 - pct);
  const computedUsd = Math.max(STRIPE_MIN_CHARGE_USD, Math.ceil(raw * 100 - 1e-9) / 100);
  const adjustUsd = Math.round((p.adjustUsd ?? 0) * 100) / 100;
  const priceUsd = Math.max(STRIPE_MIN_CHARGE_USD, Math.round((computedUsd + adjustUsd) * 100) / 100);
  const stripeFeeUsd = priceUsd * pct + p.feeFixedUsd;
  const costUsd = p.units * p.unitCostUsd;
  const profitUsd = priceUsd - stripeFeeUsd - costUsd;
  return {
    mode: p.mode,
    units: p.units,
    priceUsd,
    computedUsd,
    adjustUsd,
    stripeFeeUsd,
    costUsd,
    profitUsd,
    profitPerUnitUsd: profitUsd / p.units,
    profitPerGenerationUsd: profitUsd / (p.units * p.generationsPerUnit),
  };
}

/** The pricing settings, as the calculator needs them. */
export type PriceSettings = {
  /** The sizes of generation pack on sale, smallest first. */
  packGenerations: number[];
  /** The sizes of story pack on sale, smallest first. */
  packGames: number[];
  profitPerGenerationUsd: number;
  stripeFeePercent: number;
  stripeFeeFixedUsd: number;
  /**
   * Per-pack nudges in dollars, keyed by pack size ("10", "50") and by "game" for the story pack. Optional:
   * a settings object written before nudges existed simply has none.
   */
  priceAdjustUsd?: Record<string, number>;
};

/** The key a pack's nudge is stored under; the two modes cannot collide on a shared size (1 scene vs 1 story). */
export const adjustKey = (mode: Exclude<PurchaseMode, "off">, units: number) => (mode === "games" ? `game:${units}` : String(units));

/**
 * Both models priced from one generation's cost and the steps a game takes. Generations are sold in several
 * sizes at once: the same profit per generation in each, so the bigger packs come out cheaper per generation
 * on their own - Stripe's fixed fee is spread over more of them.
 */
export function quoteBoth(costPerGenerationUsd: number, stepsPerGame: number, s: PriceSettings) {
  const fees = { feePercent: s.stripeFeePercent, feeFixedUsd: s.stripeFeeFixedUsd };
  return {
    generations: s.packGenerations.map(units =>
      quotePack({
        mode: "generations",
        units,
        unitCostUsd: costPerGenerationUsd,
        unitProfitUsd: s.profitPerGenerationUsd,
        generationsPerUnit: 1,
        adjustUsd: s.priceAdjustUsd?.[adjustKey("generations", units)],
        ...fees,
      }),
    ),
    games: s.packGames.map(units =>
      quotePack({
        mode: "games",
        units,
        unitCostUsd: costPerGenerationUsd * stepsPerGame,
        unitProfitUsd: s.profitPerGenerationUsd * stepsPerGame,
        generationsPerUnit: stepsPerGame,
        adjustUsd: s.priceAdjustUsd?.[adjustKey("games", units)],
        ...fees,
      }),
    ),
  };
}

/** Sanitises the per-pack nudges: whole cents, within a sane range, junk dropped. */
export function readAdjustments(value: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!value || typeof value !== "object") return out;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const n = Number(raw);
    if (!Number.isFinite(n) || !n) continue;
    out[key] = Math.max(-PRICE_ADJUST_MAX, Math.min(PRICE_ADJUST_MAX, Math.round(n * 100) / 100));
  }
  return out;
}

/** How far a price may be nudged either way, so a typo cannot sell 500 scenes for nothing. */
export const PRICE_ADJUST_MAX = 100;

/** A price list as it was published: what players are charged until it is published again. */
export type PublishedPrices = {
  at: string;
  mode: Exclude<PurchaseMode, "off">;
  prices: { units: number; priceUsd: number }[];
  /**
   * The settings that produced these prices, kept so the panel can put them back: a draft that went too far
   * is undone by restoring the policy, not by guessing which number was changed.
   */
  policy?: PriceSettings;
};

/** Sanitises a published price list read back from the settings file. */
export function readPublished(value: unknown): PublishedPrices | null {
  if (!value || typeof value !== "object") return null;
  const { at, mode, prices } = value as Partial<PublishedPrices>;
  if (!at || (mode !== "generations" && mode !== "games") || !Array.isArray(prices)) return null;
  const clean = prices
    .map(p => ({ units: Math.round(Number(p?.units)), priceUsd: Math.round(Number(p?.priceUsd) * 100) / 100 }))
    .filter(p => Number.isFinite(p.units) && p.units > 0 && Number.isFinite(p.priceUsd) && p.priceUsd >= STRIPE_MIN_CHARGE_USD);
  if (!clean.length) return null;
  const saved = (value as { policy?: Partial<PriceSettings> }).policy;
  const policy: PriceSettings | undefined = saved
    ? {
        packGenerations: readPackSizes(saved.packGenerations, DEFAULT_PACK_SIZES),
        packGames: readPackSizes(saved.packGames, DEFAULT_GAME_PACK_SIZES),
        profitPerGenerationUsd: Number(saved.profitPerGenerationUsd) || 0,
        stripeFeePercent: Number(saved.stripeFeePercent) || 0,
        stripeFeeFixedUsd: Number(saved.stripeFeeFixedUsd) || 0,
        priceAdjustUsd: readAdjustments(saved.priceAdjustUsd),
      }
    : undefined;
  return { at: String(at), mode, prices: clean, ...(policy && { policy }) };
}

/** Sanitises a list of pack sizes: whole, in range, no duplicates, smallest first, never empty. */
export function readPackSizes(value: unknown, fallback: number[]): number[] {
  const list = (Array.isArray(value) ? value : [value])
    .map(n => Math.round(Number(n)))
    .filter(n => Number.isFinite(n) && n >= 1 && n <= PACK_MAX);
  const unique = [...new Set(list)].sort((a, b) => a - b).slice(0, PACK_SIZES_MAX);
  return unique.length ? unique : fallback;
}
export const NETWORK_TOLERANCE_MAX = 100;

export const ALLOWANCE_MODE_LABELS: Record<AllowanceMode, string> = {
  unlimited: "Unlimited",
  games: "Finished games",
  generations: "Generated steps",
};

/** One line of plain English for the admin panel and the player-facing gate. */
export function describeAllowance({ mode, count, resetDays }: Allowance) {
  if (mode === "unlimited") return "as much as they like";
  const unit = mode === "games" ? "game" : "generation";
  if (count === 0) return `nothing (blocked before the first ${unit})`;
  const every = resetDays > 0 ? ` every ${resetDays === 1 ? "day" : `${resetDays} days`}` : " ever";
  return `${count} ${unit}${count === 1 ? "" : "s"}${every}`;
}

/**
 * What one earned go is worth. A share buys a whole go, not a single step: in games mode that is one more
 * story, and in generations mode a fresh allowance of steps - so someone on "3 generations" who shares gets
 * 3 more, not 1. Anything less would be a worse deal than the wording promises.
 */
export const goSize = ({ mode, count }: Allowance) => (mode === "games" ? 1 : Math.max(1, count));

/**
 * LLM choices for the admin dropdowns. Speeds measured 2026-09-13 on GMI with the real diagnostic prompt
 * (~2.2k tokens in); the shot writer returns longer output, so expect it to take somewhat longer.
 */
export const LLM_MODEL_OPTIONS = [
  { id: "google/gemini-3.1-flash-lite-preview", label: "Gemini 3.1 Flash-Lite", speed: "~2s", reasoning: "light" },
  { id: "google/gemini-3.5-flash-lite", label: "Gemini 3.5 Flash-Lite", speed: "~2s", reasoning: "light+" },
  { id: "google/gemini-3.8-flash", label: "Gemini 3.8 Flash", speed: "~6s", reasoning: "deep" },
] as const;
export type LlmModelId = (typeof LLM_MODEL_OPTIONS)[number]["id"];
